import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { ChevronDown, CircleHelp } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import type { PriorityBreakdown } from "@/lovable-demo/lib/signals/priority";
import {
  assessSeverity,
  createRecordFromSignal,
  disposeSignal,
} from "@/lovable-demo/lib/signals/signals";
import {
  DEFAULT_SEVERITY_SCALE,
  DISPOSITION_LABELS,
  SIGNAL_CLASS_LABELS,
  SIGNAL_STATUS_LABELS,
  type Disposition,
  type Signal,
  type SignalClass,
  type SignalStatus,
} from "@/lovable-demo/lib/signals/types";
import { useWarranty } from "@/lovable-demo/lib/warranty/store";

const sevStyle: Record<number, string> = {
  5: "border-risk-critical/40 bg-risk-critical/15 text-risk-critical",
  4: "border-risk-high/40 bg-risk-high/15 text-risk-high",
  3: "border-risk-medium/40 bg-risk-medium/15 text-risk-medium",
  2: "border-risk-low/40 bg-risk-low/15 text-risk-low",
  1: "border-border bg-elevated text-muted-foreground",
};

export function SeverityChip({
  severity,
  pending,
}: {
  severity: number | null;
  pending?: boolean;
}) {
  if (!severity)
    return (
      <span className="inline-flex rounded border border-dashed border-border px-2 py-0.5 text-[0.68rem] uppercase tracking-wider text-muted-foreground">
        Severity not assessed
      </span>
    );
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[0.68rem] font-semibold uppercase tracking-wider",
        sevStyle[severity],
      )}
    >
      S{severity} · {DEFAULT_SEVERITY_SCALE[severity]}
      {pending && " · pending"}
    </span>
  );
}

export function ClassChip({ cls }: { cls: SignalClass }) {
  return (
    <span className="inline-flex rounded border border-primary/30 bg-primary/10 px-2 py-0.5 text-[0.66rem] font-semibold uppercase tracking-wider text-primary">
      {SIGNAL_CLASS_LABELS[cls]}
    </span>
  );
}

export function SignalStatusChip({ status }: { status: SignalStatus }) {
  const tone =
    status === "open"
      ? "border-risk-high/40 text-risk-high"
      : status === "closed-nfa"
        ? "border-border text-muted-foreground"
        : "border-primary/40 text-primary";
  return (
    <span
      className={cn(
        "inline-flex rounded border px-2 py-0.5 text-[0.66rem] uppercase tracking-wider",
        tone,
      )}
    >
      {SIGNAL_STATUS_LABELS[status]}
    </span>
  );
}

export function PriorityBars({ breakdown }: { breakdown: PriorityBreakdown }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="space-y-2">
      <p className="text-[0.7rem] text-muted-foreground">
        Advisory ordering only. The priority score never sets severity, disposition or record
        creation.
      </p>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        aria-expanded={expanded}
        onClick={() => setExpanded((value) => !value)}
        className="h-7 w-full justify-between px-2 text-muted-foreground"
      >
        <span className="inline-flex items-center gap-1.5">
          <CircleHelp aria-hidden="true" />
          Why this priority?
        </span>
        <ChevronDown
          aria-hidden="true"
          className={cn("transition-transform", expanded && "rotate-180")}
        />
      </Button>
      {expanded && (
        <div className="space-y-1.5 border-t border-border pt-2">
          <p className="pb-1 text-[0.7rem] leading-relaxed text-muted-foreground">
            The score combines only these implemented evidence factors. Each contribution shows its
            share of the advisory total.
          </p>
          {breakdown.factors.map((f) => (
            <div key={f.key} className="grid grid-cols-[10rem_1fr_3rem] items-center gap-2 text-xs">
              <span className="text-muted-foreground">{f.label}</span>
              <div className="h-1.5 overflow-hidden rounded bg-elevated">
                <div
                  className="h-full bg-primary"
                  style={{ width: `${Math.round(f.value * 100)}%` }}
                />
              </div>
              <span className="num text-right text-foreground/80">
                +{f.contribution.toFixed(1)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function SeverityPanel({ signal, onDone }: { signal: Signal; onDone: () => void }) {
  const [severity, setSeverity] = useState<number>(
    signal.currentSeverity ?? signal.aiSuggestedSeverity ?? 3,
  );
  const [rationale, setRationale] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    const res = await assessSeverity({ signal, severity, rationale });
    setSaving(false);
    if (!res.ok) {
      toast.error("Severity not recorded", { description: res.error });
      return;
    }
    setRationale("");
    toast.success(
      res.pendingSecondApproval
        ? "Severity 5 recorded — awaiting an independent second approval"
        : `Severity ${severity} confirmed in the append-only audit trail`,
    );
    onDone();
  };

  return (
    <div className="space-y-3">
      {signal.aiSuggestedSeverity && (
        <p className="text-xs text-muted-foreground">
          AI suggestion (advisory):{" "}
          <span className="num text-foreground">S{signal.aiSuggestedSeverity}</span> — a human must
          set the final severity.
        </p>
      )}
      <div className="flex flex-wrap gap-1.5">
        {[5, 4, 3, 2, 1].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => setSeverity(n)}
            className={cn(
              "rounded border px-2.5 py-1 text-xs transition",
              severity === n
                ? sevStyle[n]
                : "border-border text-muted-foreground hover:text-foreground",
            )}
          >
            {n} · {DEFAULT_SEVERITY_SCALE[n]}
          </button>
        ))}
      </div>
      <Textarea
        value={rationale}
        onChange={(e) => setRationale(e.target.value)}
        placeholder="Severity rationale and supporting evidence (required)"
        className="min-h-20 text-sm"
      />
      <Button size="sm" onClick={submit} disabled={saving || !rationale.trim()}>
        {saving ? "Recording…" : "Record severity"}
      </Button>
      {severity === 5 && (
        <p className="text-[0.7rem] text-muted-foreground">
          Severity 5 requires Quality Director authority and an independent second approval.
        </p>
      )}
    </div>
  );
}

export function DispositionPanel({ signal, onDone }: { signal: Signal; onDone: () => void }) {
  const { data, adoptRecord } = useWarranty();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [choice, setChoice] = useState<Disposition>("monitor");
  const [rationale, setRationale] = useState("");
  const [target, setTarget] = useState<string>(signal.linkedRecordId ?? "");
  const [saving, setSaving] = useState(false);

  const candidates = [...data.records]
    .sort(
      (a, b) => Number(b.programId === signal.programId) - Number(a.programId === signal.programId),
    )
    .filter((r) => r.status !== "closed");

  const submit = async () => {
    setSaving(true);
    if (choice === "create") {
      const platform =
        data.records.find((r) => r.programId === signal.programId)?.platform ??
        data.programs.find((p) => p.id === signal.programId)?.vehicleLine ??
        "";
      const res = await createRecordFromSignal({
        signal,
        rationale,
        existingRecords: data.records,
        platform,
        title: signal.title,
      });
      setSaving(false);
      if (!res.ok || !res.record) {
        toast.error("Problem Record not created", {
          description: res.ok ? "Unknown error" : res.error,
        });
        return;
      }
      adoptRecord(res.record);
      await qc.invalidateQueries();
      toast.success(
        `${res.record.code} created — decision confirmed in the append-only audit trail`,
      );
      void navigate({ to: "/demo/problem-record/$id", params: { id: res.record.id } });
      return;
    }
    const res = await disposeSignal({
      signal,
      disposition: choice,
      rationale,
      targetRecord: data.records.find((r) => r.id === target),
    });
    setSaving(false);
    if (!res.ok) {
      toast.error("Disposition not recorded", { description: res.error });
      return;
    }
    setRationale("");
    toast.success(`${DISPOSITION_LABELS[choice]} — confirmed in the append-only audit trail`);
    onDone();
  };

  const createBlocked = choice === "create" && !signal.currentSeverity;

  return (
    <div className="space-y-3">
      <div className="grid gap-1.5 sm:grid-cols-2">
        {(Object.keys(DISPOSITION_LABELS) as Disposition[]).map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => setChoice(d)}
            className={cn(
              "rounded border px-3 py-2 text-left text-xs transition",
              choice === d
                ? "border-primary bg-primary/10 text-foreground"
                : "border-border text-muted-foreground hover:text-foreground",
            )}
          >
            {DISPOSITION_LABELS[d]}
          </button>
        ))}
      </div>
      {choice === "link" && (
        <select
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          className="w-full rounded border border-border bg-elevated px-2 py-1.5 text-sm"
          aria-label="Problem Record to link"
        >
          <option value="">Select a Problem Record…</option>
          {candidates.map((r) => (
            <option key={r.id} value={r.id}>
              {r.code} — {r.title}
            </option>
          ))}
        </select>
      )}
      {createBlocked && (
        <p className="text-xs text-risk-high">
          Confirm a human severity assessment before creating a record.
        </p>
      )}
      <Textarea
        value={rationale}
        onChange={(e) => setRationale(e.target.value)}
        placeholder="Rationale for this disposition (required)"
        className="min-h-20 text-sm"
      />
      <Button size="sm" onClick={submit} disabled={saving || !rationale.trim() || createBlocked}>
        {saving ? "Recording…" : `Record decision — ${DISPOSITION_LABELS[choice]}`}
      </Button>
      <p className="text-[0.7rem] text-muted-foreground">
        When you select Record Decision, Broadview verifies your authority and securely records the
        decision, audit entry and Evidence Snapshot at Decision before confirming completion.
      </p>
    </div>
  );
}
